import React from 'react';
import { Badge, Card, Divider, Space, Tag, Tooltip, Typography } from 'antd';
import {
  AlertOutlined,
  CalendarOutlined,
  CarOutlined,
  EnvironmentOutlined,
  MessageOutlined,
  ToolOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import eventService from '../services/eventService';
import { BREAKDOWN_STATUS_COLORS } from '../utils/constants';
import '../assets/styles/eventCard.css';

const { Text, Title } = Typography;

const invalidDate = '0001-01-01T00:00:00+00:00';

const formatDate = (value) => {
  if (!value || value === invalidDate) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-ZA', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(date);
};

const calculateElapsedTime = (value) => {
  if (!value || value === invalidDate) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const hours = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60));
  if (hours < 1) return 'Less than an hour ago';
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;

  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

const getStatusColor = (value) => {
  const text = String(value || '').toLowerCase();
  if (text.includes('cancel') || text.includes('overdue')) return '#ff4d4f';
  if (text.includes('resolved') || text.includes('complete')) return '#52c41a';
  if (text.includes('progress') || text.includes('workshop')) return '#1677ff';
  if (text.includes('pending') || text.includes('created')) return '#faad14';
  return '#8c8c8c';
};

const parseMechanics = (mechanicsJson) => {
  if (!mechanicsJson) return [];
  if (Array.isArray(mechanicsJson)) return mechanicsJson;
  try {
    const parsed = JSON.parse(mechanicsJson);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const EventCard = ({ event, mechanicStatuses = [], showStatus = true }) => {
  const navigate = useNavigate();
  const normalized = eventService.normalizeEvent ? eventService.normalizeEvent(event) : event;
  const eventId = normalized.id;
  const reference = normalized.breakdownReference || 'No reference';
  const mechanics = parseMechanics(normalized.mechanicsJson);
  const getMechanicStatusName = (id) =>
    mechanicStatuses.find((status) => Number(status.id) === Number(id))?.name || 'Unknown';
  const createdDate = new Date(normalized.dateTimeOffset);
  const createdAgeHours = Number.isNaN(createdDate.getTime())
    ? 0
    : (Date.now() - createdDate.getTime()) / (1000 * 60 * 60);

  const alerts = [];
  if (normalized.hasUnCompletedDelay) {
    alerts.push({ key: 'delay', icon: <ClockCircleOutlined />, label: 'Active delay' });
  }
  if (!normalized.assignedSupplier && createdAgeHours > 1) {
    alerts.push({ key: 'supplier', icon: <ToolOutlined />, label: 'No supplier after one hour' });
  }
  if (
    normalized.fullLastNote &&
    !['consultant', 'admin'].includes(String(normalized.lastUserNoteRole || '').toLowerCase())
  ) {
    alerts.push({ key: 'note', icon: <MessageOutlined />, label: 'New note added' });
  }

  const handleClick = () => {
    if (!eventId) return;
    navigate(`/events/${eventId}`, {
      state: {
        from: '/events',
        selectedRef: reference,
        selectedId: eventId,
        selectedEvent: normalized
      }
    });
  };

  return (
    <Card className="event-card" hoverable onClick={handleClick}>
      <div className="event-card__header">
        <div className="event-card__title-block">
          <Title level={5} className="event-card__reference">{reference}</Title>
          {normalized.caseNumber ? <Text type="secondary">Case: {normalized.caseNumber}</Text> : null}
        </div>
        {showStatus && (
          <Badge
            className="event-card__status"
            color={getStatusColor(normalized.eventStatusName || normalized.statusDescription)}
            text={normalized.eventStatusName || normalized.statusDescription || 'Unknown'}
          />
        )}
        
      </div>

      <Divider className="event-card__divider" />

      <div className="event-card__content">
        <div className="event-card__row">
          <Space>
            <CarOutlined />
            <Text strong>{normalized.registrationNumber || 'No registration'}</Text>
          </Space>
          {normalized.fleetNumber ? <Text type="secondary">Fleet: {normalized.fleetNumber}</Text> : null}
        </div>

        {normalized.vinNumber ? <Text type="secondary" className="event-card__minor-secondary">VIN: {normalized.vinNumber}</Text> : null}
        <Text type="secondary" className="event-card__minor-secondary">{normalized.make} | {normalized.model} | {normalized.year}</Text>
        <Text type="secondary">{normalized.customerName || normalized.companyName || 'No customer'}</Text>
        <Text><Text strong>Issue:</Text> {normalized.description || 'No issue'}</Text>

        {normalized.eventInvolvementName || normalized.eventLocationName ? (
          <div className="event-card__row">
            {normalized.eventInvolvementName ? (
              <Space>
                <AlertOutlined />
                <Text>{normalized.eventInvolvementName}</Text>
              </Space>
            ) : null}
            {normalized.eventLocationName ? (
              <Space>
                <EnvironmentOutlined />
                <Text>{normalized.eventLocationName}</Text>
              </Space>
            ) : null}
          </div>
        ) : null}

        {mechanics.length > 0 ? (
          <div>
            <Text strong>Mechanics: </Text>
            <Space size={6} wrap>
              {mechanics.map((mechanic) => {
                const status = mechanic.MechanicStatus;
                const color = BREAKDOWN_STATUS_COLORS[status] || '#999';
                const statusLabel = getMechanicStatusName(status);
                const key = mechanic.MechanicId || mechanic.MechanicName;

                return (
                  <Badge
                    key={key}
                    color={color}
                    text={`${mechanic.MechanicName || 'Mechanic'} (${statusLabel})`}
                  />
                );
              })}
            </Space>
          </div>
        ) : null}

        <div className="event-card__row">
          <Space>
            <CalendarOutlined />
            <Text type="secondary">
              {calculateElapsedTime(normalized.dateTimeOffset) || formatDate(normalized.dateTimeOffset) || 'No date'}
            </Text>
          </Space>
        </div>

        {normalized.eventProviderStatusName ? (
          <Badge color={getStatusColor(normalized.eventProviderStatusName)} text={`Provider: ${normalized.eventProviderStatusName}`} />
        ) : null}

        <div className="event-card__row event-card__event-address-row">
          <Space>
            <EnvironmentOutlined />
            <Text type="secondary" className="event-card__event-address">{normalized.breakdownAddress || 'No location'}</Text>
          </Space>
        </div>
      </div>
    </Card>
  );
};

export default EventCard;
